import dayjs from 'dayjs';

/**
 * New unified date utilities - Day.js only, clean and simple
 * Replaces the complex moment.js + dayjs mixed system
 */

// Convert Day.js date to ISO string for storage
export const dayjsToISO = (dayjsDate) => {
  if (!dayjsDate || !dayjs.isDayjs(dayjsDate)) return null;
  return dayjsDate.format('YYYY-MM-DD');
};

// Convert ISO string to Day.js object
export const isoToDayjs = (isoString) => {
  if (!isoString || typeof isoString !== 'string') return null;
  const date = dayjs(isoString, 'YYYY-MM-DD', true);
  return date.isValid() ? date : null;
};

// Format Day.js date for PDF display
export const formatForPDF = (dayjsDate) => {
  if (!dayjsDate || !dayjs.isDayjs(dayjsDate)) return '';
  return dayjsDate.format('DD/MM/YYYY');
};

// Format date range for PDF display
export const formatDateRangeForPDF = (startDate, endDate) => {
  const start = isoToDayjs(startDate);
  const end = isoToDayjs(endDate);

  if (!start || !end) return '';

  return `${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`;
};

// Format duration for PDF display
export const formatDurationForPDF = (duration, unit) => {
  if (!duration || !unit) return '';
  return `${duration} ${unit}`;
};

// Validate Day.js date
export const isValidDayjsDate = (date) => {
  return date && dayjs.isDayjs(date) && date.isValid();
};

// Validate date range (start before end)
export const isValidDateRange = (startDate, endDate) => {
  const start = isoToDayjs(startDate);
  const end = isoToDayjs(endDate);

  if (!start || !end) return false;
  return start.isBefore(end) || start.isSame(end);
};

// Get current date as Day.js object
export const getCurrentDayjs = () => {
  return dayjs();
};

// Add time to date
export const addToDate = (date, amount, unit) => {
  const dayjsDate = isoToDayjs(date);
  if (!dayjsDate) return null;
  return dayjsDate.add(amount, unit);
};

// Check if date is in the future
export const isFutureDate = (date) => {
  const dayjsDate = isoToDayjs(date);
  if (!dayjsDate) return false;
  return dayjsDate.isAfter(dayjs());
};

// Check if date is in the past
export const isPastDate = (date) => {
  const dayjsDate = isoToDayjs(date);
  if (!dayjsDate) return false;
  return dayjsDate.isBefore(dayjs());
};

// Get difference between two dates
export const getDateDifference = (startDate, endDate, unit = 'day') => {
  const start = isoToDayjs(startDate);
  const end = isoToDayjs(endDate);

  if (!start || !end) return 0;
  return end.diff(start, unit);
};

// Format multiple dates for PDF (comma-separated)
export const formatMultipleDatesForPDF = (dates) => {
  if (!dates || !Array.isArray(dates) || dates.length === 0) return '';

  const formattedDates = dates.map(entry => {
    switch (entry.type) {
      case 'date':
        return entry.value ? formatForPDF(isoToDayjs(entry.value)) : 'TBC';

      case 'range':
        if (entry.value && entry.endValue) {
          return formatDateRangeForPDF(entry.value, entry.endValue);
        }
        return 'TBC';

      case 'duration':
        if (entry.duration && entry.durationUnit) {
          return formatDurationForPDF(entry.duration, entry.durationUnit);
        }
        return 'TBC';

      case 'months':
        if (entry.duration) {
          return `${entry.duration} month${entry.duration > 1 ? 's' : ''}`;
        }
        return 'TBC';

      case 'tbc':
        return 'TBC';

      default:
        return 'TBC';
    }
  }).filter(date => date); // Remove empty entries

  return formattedDates.join(', ');
};

// Validate unified date entry
export const validateDateEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return false;

  switch (entry.type) {
    case 'date':
      return entry.value && isoToDayjs(entry.value);

    case 'range':
      return entry.value && entry.endValue &&
             isoToDayjs(entry.value) && isoToDayjs(entry.endValue) &&
             isValidDateRange(entry.value, entry.endValue);

    case 'duration':
      return entry.duration && entry.duration > 0 && entry.durationUnit;

    case 'months':
      return entry.duration && entry.duration > 0;

    case 'tbc':
      return true; // TBC doesn't need validation

    default:
      return false;
  }
};

// Validate entire dates array
export const validateDatesArray = (dates) => {
  if (!dates || !Array.isArray(dates)) return false;
  if (dates.length === 0) return true; // Empty array is valid for new documents

  return dates.every(validateDateEntry);
};

// Create empty date entry for new documents
export const createEmptyDateEntry = (documentType, type = null) => {
  const defaultType = type || getDefaultDateType(documentType);

  return {
    id: Date.now(),
    type: defaultType,
    value: null,
    endValue: null,
    duration: null,
    durationUnit: 'days'
  };
};

// Get default date type for document type
export const getDefaultDateType = (documentType) => {
  switch (documentType) {
    case 'purchase':
      return 'date';
    case 'scaffolding_service':
      return 'date';
    case 'rental':
    case 'rental_extension':
      return 'range'; // Fixed: rental quotations should default to 'range' not 'date'
    case 'damaged_items':
      return 'date';
    default:
      return 'date';
  }
};

// Convert legacy date info to readable string (for migration)
export const createLegacyDateString = (document) => {
  const parts = [];

  // Convert all legacy date fields to readable format
  if (document.leadTime) {
    parts.push(`Lead Time: ${formatForPDF(isoToDayjs(document.leadTime))}`);
  }

  if (document.serviceDate) {
    parts.push(`Service: ${formatForPDF(isoToDayjs(document.serviceDate))}`);
  }

  if (document.rentalStartDate && document.rentalEndDate) {
    parts.push(`Rental: ${formatDateRangeForPDF(document.rentalStartDate, document.rentalEndDate)}`);
  }

  if (document.rentalDuration && document.rentalDurationUnit) {
    parts.push(`Duration: ${formatDurationForPDF(document.rentalDuration, document.rentalDurationUnit)}`);
  }

  if (document.leadTimeMonths) {
    parts.push(`Lead Time: ${document.leadTimeMonths} month${document.leadTimeMonths > 1 ? 's' : ''}`);
  }

  return parts.join(', ');
};

// Debug helper
export const debugDateEntry = (entry, label = 'Date Entry') => {
  console.log(`${label}:`, {
    id: entry.id,
    type: entry.type,
    value: entry.value,
    endValue: entry.endValue,
    duration: entry.duration,
    durationUnit: entry.durationUnit,
    isValid: validateDateEntry(entry)
  });
};
